import React, { useState, useEffect } from 'react';
import {
  X, MapPin, Maximize, Maximize2, CheckCircle, Bed, Bath, Droplets, Car,
  Building, Sun, Zap, Shield, Leaf, Ruler, Home,
  Layers, Box, MessageCircle, Mail, Calendar, ChevronLeft,
  ChevronRight, Heart, Share2, Printer, Download
} from 'lucide-react';
import CalendarModal from './CalendarModal'; // Agrega este import
import SafeImage from './SafeImage';
import { formatImageUrl } from '../services/api';
import { getFavorites, toggleFavorite } from '../utils/favorites';

const FEATURE_ICONS = {
  maximize: Maximize,
  'check-circle': CheckCircle,
  bed: Bed,
  bath: Bath,
  droplets: Droplets,
  car: Car,
  building: Building,
  sun: Sun,
  zap: Zap,
  shield: Shield,
  tree: Leaf,
  leaf: Leaf,
  ruler: Ruler,
  home: Home,
  layers: Layers,
  box: Box
};

const PropertyDetailsModal = ({ property, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
   const [showCalendar, setShowCalendar] = useState(false); // Estado para calendario
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    if (property) {
      setIsFavorite(getFavorites().includes(property.id));
    }
  }, [property]);

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  if (!isOpen || !property) return null;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (deltaX > 50) handlePrevImage();
    else if (deltaX < -50) handleNextImage();
    setTouchStartX(null);
  };

  const handleShare = () => {
    const propertyUrl = `${window.location.origin}/propiedad/${property.id}`;

    if (navigator.share) {
      navigator.share({
        title: property.title,
        text: property.description,
        url: propertyUrl,
      });
    } else {
      navigator.clipboard.writeText(propertyUrl);
      alert('Enlace copiado al portapapeles');
    }
  };

   const handleScheduleVisit = () => {
    setShowCalendar(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl animate-modal-in">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-foreground">{property.title}</h2>
              <p className="text-muted-foreground flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {property.location}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsFavorite(toggleFavorite(property.id))}
                className={`p-2 rounded-full border-2 transition-colors ${
                  isFavorite
                    ? 'bg-red-50 border-red-400 hover:bg-red-100'
                    : 'border-transparent hover:bg-gray-100'
                }`}
                aria-label={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
              >
                <Heart className={`w-6 h-6 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
              </button>
              
              <button 
                onClick={handleShare}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Compartir propiedad"
              >
                <Share2 className="w-6 h-6 text-gray-500" />
              </button>
              
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Cerrar detalles"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </div>
          
          {/* Contenido scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Galería de imágenes */}
            <div className="relative h-96 bg-gray-100">
              <SafeImage
                src={property.images[currentImageIndex]}
                alt={`${property.title} - Imagen ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Botón pantalla completa */}
              <button
                onClick={() => setIsFullscreen(true)}
                className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                aria-label="Ver en pantalla completa"
              >
                <Maximize2 className="w-5 h-5 text-gray-700" />
              </button>

              {/* Controles de navegación de imágenes */}
              <button 
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                aria-label="Imagen anterior"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button 
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                aria-label="Imagen siguiente"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Indicadores de imágenes */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {property.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-white w-4' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Ir a imagen ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Miniaturas */}
              <div className="absolute bottom-4 right-4 flex gap-2">
                {property.images.slice(0, 3).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex 
                        ? 'border-primary' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <SafeImage
                      src={img}
                      alt={`Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
                {property.images.length > 3 && (
                  <div className="w-16 h-16 rounded-lg bg-black/60 flex items-center justify-center text-white font-bold">
                    +{property.images.length - 3}
                  </div>
                )}
              </div>
            </div>
            
            {/* Contenido principal */}
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Columna izquierda: Información principal */}
                <div className="md:col-span-2">
                  {/* Precio y estado */}
                  <div className="mb-8">
                    <div className="flex items-baseline gap-4 mb-2">
                      <span className="text-4xl font-bold text-primary">
                        {property.price}
                        {property.rental && <span className="text-lg font-normal text-muted-foreground">/mes</span>}
                      </span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        {property.status}
                      </span>
                    </div>
                    
                    {property.contractDetails && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                        <h4 className="font-semibold text-blue-800 mb-2">Condiciones de alquiler:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-blue-600 font-medium">Depósito:</span>
                            <p className="text-blue-800">{property.contractDetails.deposit}</p>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Duración:</span>
                            <p className="text-blue-800">{property.contractDetails.duration}</p>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Expensas:</span>
                            <p className="text-blue-800">{property.contractDetails.expenses}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Descripción */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-foreground">Descripción</h3>
                    <p className="text-gray-700 leading-relaxed">{property.description}</p>
                  </div>
                  
                  {/* Características destacadas */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-foreground">Características Destacadas</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {property.features.map((feature, index) => {
                        const Icon = FEATURE_ICONS[feature.icon] || CheckCircle;
                        return (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all"
                          >
                            <div className="p-2.5 bg-primary rounded-xl flex-shrink-0">
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-foreground leading-tight">{feature.value || feature.text}</p>
                              <p className="text-sm text-gray-600">{feature.label || feature.detail}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Comodidades y servicios */}
                  {property.amenities && property.amenities.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold mb-4 text-foreground">Comodidades</h3>
                      <div className="flex flex-wrap gap-2">
                        {property.amenities.map((amenity, index) => (
                          <span 
                            key={index} 
                            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Columna derecha: Información de contacto y acción */}
                <div className="space-y-6">
                  {/* Tarjeta de contacto */}
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h4 className="text-lg font-bold mb-4 text-foreground">Contactar al agente</h4>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-3">
                        {property.contactAvatar ? (
                          <img
                            src={formatImageUrl(property.contactAvatar)}
                            alt={property.contactPerson}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-primary font-bold text-lg">
                              {property.contactPerson.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-foreground">{property.contactPerson}</p>
                          <p className="text-sm text-gray-600">Agente inmobiliario</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <a
                          href={`https://wa.me/${property.contactPhone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, necesito info de ${property.title} - ${property.address || property.location}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <MessageCircle className="w-5 h-5 text-primary" />
                          <div>
                            <span className="font-medium block">{property.contactPhone}</span>
                            <span className="text-xs text-gray-500">Escribir por WhatsApp</span>
                          </div>
                        </a>

                        <a
                          href={`mailto:consultas@sanzbergerbienesraices.com.ar?subject=${encodeURIComponent(`Consulta sobre ${property.title}`)}&body=${encodeURIComponent(`Hola, quiero más información sobre ${property.title} (${property.address || property.location}).`)}`}
                          className="w-full flex items-center justify-center gap-3 p-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium"
                        >
                          <Mail className="w-5 h-5" />
                          Enviar mensaje
                        </a>
                      </div>
                    </div>
                    
                    {/* Información adicional */}
                    <div className="pt-6 border-t border-gray-200 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Año:</span>
                        <span className="font-medium">{property.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Dirección:</span>
                        <span className="font-medium text-right">{property.address}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Referencia:</span>
                        <span className="font-medium">SB-{property.id.toString().padStart(3, '0')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Botones de acción */}
                  <div className="space-y-3">
                    <button 
  onClick={handleScheduleVisit}
  className="w-full py-4 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
>
  <Calendar className="w-5 h-5" />
  Agendar visita
</button>
                    
                    <button 
                      onClick={() => window.print()}
                      className="w-full py-3 border border-gray-300 text-foreground rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Printer className="w-5 h-5" />
                      Imprimir detalles
                    </button>
                    
                    <button 
                      onClick={() => alert('Generando ficha técnica en PDF...')}
                      className="w-full py-3 border border-gray-300 text-foreground rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Descargar ficha
                    </button>
                  </div>
                  
                  {/* Ubicación */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-bold mb-4 text-foreground">Ubicación aproximada</h4>
                    <div className="h-48 rounded-xl overflow-hidden">
                      <iframe
                        title={`Mapa de ${property.location}`}
                        src={
                          property.mapLat && property.mapLng
                            ? `https://www.google.com/maps?q=${property.mapLat},${property.mapLng}&z=16&output=embed`
                            : `https://www.google.com/maps?q=${encodeURIComponent(property.location)}&output=embed`
                        }
                        className="w-full h-full border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                      <p className="font-medium text-sm">{property.location}</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      * La ubicación exacta se proporciona en la visita
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Propiedades similares */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-6 text-foreground">Propiedades similares</h3>
                <p className="text-gray-600">¿Interesado en ver propiedades similares? <button className="text-primary font-medium hover:underline">Ver más opciones</button></p>
              </div>
               {/* Modal de calendario */}
  <CalendarModal
    isOpen={showCalendar}
    onClose={() => setShowCalendar(false)}
    propertyTitle={property.title}
    propertyId={property.id}
    contactPerson={property.contactPerson}
    contactPhone={property.contactPhone}
  />
            </div>
          </div>
        </div>
      </div>

      {/* Visor de imagen en pantalla completa */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            aria-label="Cerrar pantalla completa"
          >
            <X className="w-6 h-6" />
          </button>

          {property.images.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                aria-label="Imagen anterior"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                aria-label="Imagen siguiente"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          <SafeImage
            src={property.images[currentImageIndex]}
            alt={`${property.title} - Imagen ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain px-4"
          />

          {property.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium">
              {currentImageIndex + 1} / {property.images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetailsModal;